import { useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IssuePriority } from '@d-orca/shared/trusstor';
import { OWNS_ERROR_FEEDBACK } from '@d-orca/shared/client/hooks';
import {
  assigneeName,
  assigneesFor,
  dirtyDiff,
  formReducer,
  professionsFor,
  toCreateBody,
  withProjectDueDate,
  type FormState,
} from '@d-orca/shared/client/issues';
import type { LinkedTask } from '@store/slices/dialog.slice';
import { DiscardModal } from '@components/ui/DiscardModal/DiscardModal';
import { DateField } from '@components/ui/DateField/DateField';
import { useCreateIssue, useUpdateIssue, useSiteUsers, useSiteAreas, useSiteProfessions } from '../hooks';
import { DescriptionField } from './DescriptionField';
import { ProfessionSelector } from './ProfessionSelector';
import { AssigneeSelector } from './AssigneeSelector';
import { LocationSelector } from './LocationSelector';
import { IconToggle } from './IconToggle';
import { PrivateField } from './PrivateField';
import { useDismissGuard } from './useDismissGuard';
import styles from './IssueFormDialog.module.css';

export interface IIssueFormProps {
  /** The issue being edited; null for a new one. */
  issueId: string | null;
  siteId: string | null;
  /** What the form opens with. Read ONCE, on mount: the host keys the form per
   *  open, so a refetch or a rolled-back optimistic write while it is open never
   *  overwrites what the user is typing. */
  seed: FormState;
  tz: string | undefined;
  linkedTask: LinkedTask | null;
  onClose: () => void;
}

/** The issue create/edit form, inside its dialog chrome. */
export function IssueForm({ issueId, siteId, seed, tz, linkedTask, onClose }: IIssueFormProps) {
  const { t } = useTranslation();
  const [initialState] = useState(seed);
  const [state, formDispatch] = useReducer(formReducer, initialState);

  const usersQuery = useSiteUsers(siteId);
  const areas = useSiteAreas(siteId).data?.areas;
  const profsQuery = useSiteProfessions(siteId);
  // Both report a failure inline (below), keeping the dialog and its values.
  const create = useCreateIssue(siteId, OWNS_ERROR_FEEDBACK);
  const update = useUpdateIssue(siteId, OWNS_ERROR_FEEDBACK);

  const patch = dirtyDiff(initialState, state);
  const dirty = Object.keys(patch).length > 0;
  const guard = useDismissGuard(dirty, onClose);

  const onSave = () => {
    if (issueId === null) {
      create.mutate(withProjectDueDate(toCreateBody(state), tz), { onSuccess: onClose });
    } else if (!dirty) {
      onClose();
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      update.mutate({ issueId, patch: withProjectDueDate(patch, tz) as any }, { onSuccess: onClose });
    }
  };

  const isCreate = issueId === null;
  const users = usersQuery.data?.users ?? [];
  // The profession and assignee lists narrow each other, so neither pick can
  // leave the other out of bounds.
  const assignee = users.find((u) => u.username === state.assignee?.username);
  const isSaving = create.isPending || update.isPending;
  const saveFailed = create.isError || update.isError;
  // Only a description is required, as in Trusstor — real text, in create AND
  // edit (blank-out was saving title-less cards). An issue with no profession
  // is no trade's: Trusstor shows it to no Limited user. Gated on the site
  // zone too: the due date can't be anchored to the site's day without it.
  const canSave = !isSaving && !!tz && state.description.trim().length > 0;
  // Trusstor can't clear a STORED due date or profession (issues.service.ts,
  // CLEARED) — offering "clear" there silently snapped the value back. A value
  // picked in this session (or on a new issue) can still be cleared.
  const canClearDueDate = isCreate || !initialState.dueDate;
  const canClearProfession = isCreate || !initialState.profession;

  const hasLocation = state.locations.length > 0;

  return (
    <>
      {/* The backdrop takes the dialog's Escape too (keys bubble up from any
          field); the dialog is focusable so a click on its body keeps focus,
          and so Escape, inside it. */}
      <div className={styles.backdrop} role="presentation" {...guard.backdropProps}>
        <div className={styles.dialog} role="dialog" aria-modal="true" tabIndex={-1}>

          {/* Icons row — priority (star) + safety (shield) */}
          <div className={styles.iconsRow}>
            <IconToggle
              icon="star"
              active={state.priority === IssuePriority.CRITICAL}
              onChange={(next) =>
                formDispatch({
                  type: 'set',
                  field: 'priority',
                  value: next ? IssuePriority.CRITICAL : IssuePriority.REGULAR,
                })
              }
              tooltipActive={t('issues.action.markRegular')}
              tooltipInactive={t('issues.action.markCritical')}
            />
            <IconToggle
              icon="shield"
              active={state.isSafety}
              onChange={(next) => formDispatch({ type: 'set', field: 'isSafety', value: next })}
              tooltipActive={t('issues.action.unmarkSafety')}
              tooltipInactive={t('issues.action.markSafety')}
              disabled={!hasLocation}
            />
          </div>

          {/* Description heading + single-line input */}
          <div className={styles.descriptionSection}>
            <h2 className={styles.descriptionHeading}>
              {t('issues.field.description')}
              <span className={styles.descriptionRequired}>*</span>
            </h2>
            <DescriptionField
              value={state.description}
              onChange={(v) => formDispatch({ type: 'set', field: 'description', value: v })}
            />
            {linkedTask && (
              <p className={styles.linkedTask}>
                <span className={styles.linkedTaskLabel}>{t('issues.field.linkedTask')}</span>
                {linkedTask.title}
              </p>
            )}
          </div>

          {/* Metadata section */}
          <div className={styles.metadataSection}>
            {/* Profession */}
            <div className={styles.metadataRow}>
              <ProfessionSelector
                value={state.profession}
                onChange={(v) => formDispatch({ type: 'set', field: 'profession', value: v })}
                options={professionsFor(profsQuery.data?.professions ?? [], assignee)}
                clearable={canClearProfession}
              />
            </div>

            {/* Location */}
            <div className={styles.metadataRow}>
              <LocationSelector
                value={state.locations}
                onChange={(v) => formDispatch({ type: 'set', field: 'locations', value: v })}
                options={areas ?? []}
                disabled={!areas}
              />
            </div>

            {/* Assignee */}
            <div className={styles.metadataRow}>
              <AssigneeSelector
                value={state.assignee && { ...state.assignee, name: assigneeName(users, state.assignee) }}
                onChange={(v) => formDispatch({ type: 'set', field: 'assignee', value: v })}
                options={assigneesFor(users, state.profession?._id)}
              />
            </div>

            {/* Due date — uses the shared DateField primitive. The ui/DateField
                contract is non-nullable on change, so a separate clear button
                keeps the field optional (matches trusstor's UX). */}
            <div className={styles.metadataRow}>
              <div className={styles.dueDateWrap}>
                <DateField
                  value={state.dueDate}
                  onChange={(v) => formDispatch({ type: 'set', field: 'dueDate', value: v })}
                  placeholder={t('issues.field.dueDate')}
                  ariaLabel={t('issues.field.dueDate')}
                />
                {state.dueDate && canClearDueDate && (
                  <button
                    type="button"
                    className={styles.dueDateClear}
                    onClick={() => formDispatch({ type: 'set', field: 'dueDate', value: null })}
                    aria-label={t('issues.action.clearDueDate')}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            {/* Add Media — Phase 3 placeholder */}
            <div className={styles.metadataRow}>
              <button type="button" className={styles.addMediaBtn} disabled>
                <span className={styles.addMediaText}>{t('issues.field.mediaPlaceholder')}</span>
                <svg
                  viewBox="0 0 16 16"
                  width="12"
                  height="12"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    d="M4 6l4 4 4-4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Private checkbox — visible in both create + edit. Trusstor's
              updateIssue accepts isPrivate so existing issues can be toggled. */}
          <div className={styles.privateDivider} />
          <div className={styles.privateRow}>
            <PrivateField
              value={state.isPrivate}
              onChange={(v) => formDispatch({ type: 'set', field: 'isPrivate', value: v })}
            />
          </div>

          {/* A failed save keeps the dialog and every typed value: say so. */}
          {saveFailed && (
            <p className={styles.saveError} role="alert">
              {t(isCreate ? 'issues.dialog.createError' : 'issues.dialog.saveError')}
            </p>
          )}

          {/* Footer */}
          <footer className={styles.footer}>
            <button type="button" className={styles.cancelBtn} onClick={onClose}>
              {t('issues.dialog.cancel')}
            </button>
            <button
              type="button"
              className={styles.saveBtn}
              onClick={onSave}
              disabled={!canSave}
            >
              {isCreate ? t('issues.action.create') : t('issues.dialog.save')}
            </button>
          </footer>
        </div>
      </div>
      <DiscardModal
        open={guard.confirming}
        onConfirm={guard.discard}
        onCancel={guard.keepEditing}
        title={t('issues.dialog.discardTitle')}
        description={t('issues.dialog.discardBody')}
        confirmLabel={t('issues.dialog.discard')}
        cancelLabel={t('issues.dialog.keepEditing')}
      />
    </>
  );
}
